import { useLogto } from "@logto/react";
import { merge } from "moderndash";
import { useCallback, useEffect, useState } from "react";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { env } from "@/env";
import { useUser } from "@/hooks/useUser";

export interface BaseState<SerializedData> {
  data: SerializedData | null;
  loading: boolean;
  error: any;
}

export interface State<SerializedData> extends BaseState<SerializedData> {
  refetch: () => Promise<void>;
}

export interface LazyState<SerializedData> extends BaseState<SerializedData> {
  request: SerializedRequestHandler<SerializedData>;
}

export type SerializedRequestHandler<SerializedData> = (
  request?: SerializedRequest<any>,
) => Promise<SerializedResponse<SerializedData>>;

export interface SerializedRequest<Body> {
  path?: string;
  body?: Body;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  headers?: Record<string, string>;
}

export interface SerializedResponse<SerializedData> {
  data?: SerializedData;
  error?: Error;
}

export class RequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly data: any,
  ) {
    super(`${status}: ${statusText}`);
  }
}

export const BACKEND_BASE_URL = env.VITE_APP_BACKEND_URL;

/** Logto resource URL of the backend -
 * this is not the same as the URL where the backend is hosted, but rather the identifier configured for the resource in Logto */
export const BACKEND_RESOURCE = "https://backend.gowoop.de";

export function composeBackendUrl(path: string) {
  return BACKEND_BASE_URL + (path.startsWith("/") ? path : `/${path}`);
}

export const useLazyBackendRequest = <SerializedData extends SerializedRequestHandler<SerializedData>>(
  path?: string,
  lazyRequest?: SerializedRequest<any>,
): LazyState<SerializedData> => {
  const [data, setData] = useState<SerializedData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const { getAccessToken, clearAccessToken } = useLogto();
  const { organization } = useImpersonation();
  const { canSeeImpersonation } = useUser();

  const makeRequest: SerializedRequestHandler<SerializedData> = useCallback(
    async (serializedRequest?: SerializedRequest<any>): Promise<SerializedResponse<SerializedData>> => {
      setLoading(true);
      const backendPath = serializedRequest?.path ?? path;
      if (backendPath == null) {
        throw new Error(
          "No backend path provided. Please set the path in the hook or pass it as an argument to the request function.",
        );
      }

      const buildOptionsWithToken = (
        accessToken: string | null | undefined,
      ): RequestInit => {
        const baseOptions: { headers: Record<string, string> } = {
          headers: {
            "Content-Type": "application/json",
            ...(organization != null && canSeeImpersonation
              ? { "x-role-override": "customer" }
              : {}),
          },
        };
        if (
          accessToken != null &&
          accessToken !== "" &&
          accessToken.trim() !== ""
        ) {
          baseOptions.headers.Authorization = `Bearer ${accessToken}`;
        }

        if (serializedRequest?.body instanceof FormData) {
          delete baseOptions.headers["Content-Type"];
        } else {
          baseOptions.headers["Content-Type"] = "application/json";
        }

        return serializedRequest != null
          ? merge(serializedRequest, baseOptions, lazyRequest ?? {})
          : merge(baseOptions, lazyRequest ?? {});
      };

      const url = composeBackendUrl(backendPath);
      let token = await getAccessToken(BACKEND_RESOURCE);
      let response = await fetch(url, buildOptionsWithToken(token));

      // Logto caches expiry from `expires_in`, not always the resource JWT `exp`.
      // If the API rejects a not-yet-expired-by-client token, drop cache once and refresh.
      if (response.status === 401) {
        await clearAccessToken();
        token = await getAccessToken(BACKEND_RESOURCE);
        response = await fetch(url, buildOptionsWithToken(token));
      }

      try {
        const data = await response.json();
        if (response.ok) {
          setData(data);
          return {
            data,
          };
        } else {
          const error = new RequestError(response.status, response.statusText, data);
          setError(error);
          return {
            error: error,
          };
        }
      } catch (e) {
        if (e instanceof Error) {
          setError(e);
          return {
            error: e,
          };
        }
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [getAccessToken, clearAccessToken, path, lazyRequest, organization, canSeeImpersonation],
  );

  return {
    data,
		request: makeRequest,
    loading,
    error,
  };
};

export const useBackendRequest = <SerializedData extends SerializedRequestHandler<SerializedData>>(
  path: string,
  disabled?: boolean,
): State<SerializedData> => {
  const state = useLazyBackendRequest<SerializedData>(path);

  useEffect(() => {
    if (!disabled) state.request();
  }, [path, disabled]);

  return {
    ...state,
    refetch: async () => {
      if (!disabled) await state.request();
    },
  };
};
